import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { updatePatientData } from '@/store/slices/formSlice'
import { FormSection } from '@/types/FormSectionInterface'
import { CollectionType, OrderFormInfoData, PatientData, Sex } from '@/types/RequisitionFormTypes'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import React, { forwardRef, useImperativeHandle } from 'react'

interface PatientInformationProps {
  initialData?: Record<string, unknown>
  onDataChange?: (data: PatientData) => void
  data?: OrderFormInfoData
}

const PatientInformation = forwardRef<FormSection, PatientInformationProps>(
  ({ initialData, onDataChange, data }, ref) => {
    const dispatch = useAppDispatch()
    const { formData } = useAppSelector((state) => state.form)

    const formatDateForInput = (date: Date | null | undefined | unknown): string => {
      if (!date) return ''
      if (typeof date === 'string') return date
      if (date instanceof Date) return date.toISOString().split('T')[0]
      return ''
    }

    // Use Redux state as the source of truth, fallback to props for initial data
    const patientData = formData.patient || {
      firstName: String(initialData?.firstName || data?.patient?.name?.firstName || ''),
      lastName: String(initialData?.lastName || data?.patient?.name?.lastName || ''),
      middleName: String(initialData?.middleName || data?.patient?.name?.middleName || ''),
      dateOfBirth: formatDateForInput(initialData?.birthDate || data?.patient?.birthDate),
      sex: (initialData?.sex as Sex) || data?.patient?.sex || Sex.Unknown,
      mrn: String(initialData?.mrn || data?.patient?.mrn || ''),
      collectionType: (initialData?.collectionType as CollectionType) || CollectionType.Unknown,
    }

    // Initialize Redux state with initial data if not already set
    React.useEffect(() => {
      // Check if patient data is empty (all fields are empty strings or default values)
      const isPatientDataEmpty =
        !formData.patient ||
        (!formData.patient.firstName &&
          !formData.patient.lastName &&
          !formData.patient.mrn &&
          formData.patient.sex === Sex.Unknown &&
          formData.patient.collectionType === CollectionType.Unknown)

      if (isPatientDataEmpty && (initialData || data)) {
        const initialPatientData: PatientData = {
          firstName: String(initialData?.firstName || data?.patient?.name?.firstName || ''),
          lastName: String(initialData?.lastName || data?.patient?.name?.lastName || ''),
          middleName: String(initialData?.middleName || data?.patient?.name?.middleName || ''),
          dateOfBirth: formatDateForInput(initialData?.birthDate || data?.patient?.birthDate),
          sex: (initialData?.sex as Sex) || data?.patient?.sex || Sex.Unknown,
          mrn: String(initialData?.mrn || data?.patient?.mrn || ''),
          collectionType: (initialData?.collectionType as CollectionType) || CollectionType.Unknown,
        }
        dispatch(updatePatientData(initialPatientData))
      }
    }, [formData.patient, initialData, data, dispatch])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target
      let updateData: Partial<PatientData>
      if (name === 'sex') {
        updateData = { [name]: value as Sex }
      } else if (name === 'collectionType') {
        updateData = { [name]: value as CollectionType }
      } else {
        updateData = { [name]: value as string }
      }

      // Update Redux state
      dispatch(updatePatientData(updateData))

      // Also call the callback for backward compatibility
      const newData = { ...patientData, ...updateData } as PatientData
      onDataChange?.(newData)
    }

    // Implement the FormSection interface
    useImperativeHandle(ref, () => ({
      isSectionComplete: () => {
        return FormCompletionChecker.isPatientComplete(patientData as PatientData)
      },
      renderSection: () => {
        return (
          <div className="space-y-8">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="section-header border-b border-gray-200 pb-2">Basic Information</h3>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label htmlFor="firstName" className="form-label mb-1 block">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={patientData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="First Name"
                  />
                </div>

                <div>
                  <label htmlFor="middleName" className="form-label mb-1 block">
                    Middle Name
                  </label>
                  <input
                    type="text"
                    id="middleName"
                    name="middleName"
                    value={patientData.middleName}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Middle Name"
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="form-label mb-1 block">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={patientData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Last Name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label htmlFor="dateOfBirth" className="form-label mb-1 block">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={patientData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label htmlFor="sex" className="form-label mb-1 block">
                    Sex <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="sex"
                    name="sex"
                    value={patientData.sex as Sex}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="" disabled>
                      Sex
                    </option>
                    <option value={Sex.Male}>{Sex.Male}</option>
                    <option value={Sex.Female}>{Sex.Female}</option>
                    <option value={Sex.Ambiguous}>{Sex.Ambiguous}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="mrn" className="form-label mb-1 block">
                    MRN <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="mrn"
                    name="mrn"
                    value={patientData.mrn}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="MRN"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="section-header border-b border-gray-200 pb-2">Collection</h3>

              <div className="space-y-3">
                <label className="form-label block">
                  Collection Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label
                    className={`flex cursor-pointer items-center rounded-lg border-2 p-4 transition-colors ${
                      patientData.collectionType === 'whole-blood-edta'
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="collectionType"
                      value="whole-blood-edta"
                      checked={patientData.collectionType === 'whole-blood-edta'}
                      onChange={handleInputChange}
                      className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="body-text ml-3">Whole Blood (EDTA)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )
      },
      renderSectionInfo: () => {
        return <div></div>
      },
    }))

    // Render the form directly instead of returning null
    return (
      <div className="space-y-8">
        {/* Basic Information Section */}
        <div className="space-y-4">
          <h3 className="section-header border-b border-gray-200 pb-2">Basic Information</h3>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="firstName" className="form-label mb-1 block">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={patientData.firstName}
                onChange={handleInputChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="First Name"
              />
            </div>

            <div>
              <label htmlFor="middleName" className="form-label mb-1 block">
                Middle Name
              </label>
              <input
                type="text"
                id="middleName"
                name="middleName"
                value={patientData.middleName}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Middle Name"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="form-label mb-1 block">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={patientData.lastName}
                onChange={handleInputChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Last Name"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="dateOfBirth" className="form-label mb-1 block">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={patientData.dateOfBirth}
                onChange={handleInputChange}
                required
                max={new Date().toISOString().split('T')[0]}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="sex" className="form-label mb-1 block">
                Sex <span className="text-red-500">*</span>
              </label>
              <select
                id="sex"
                name="sex"
                value={patientData.sex as Sex}
                onChange={handleInputChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="" disabled>
                  Sex
                </option>
                <option value={Sex.Male}>{'Male'}</option>
                <option value={Sex.Female}>{'Female'}</option>
                <option value={Sex.Ambiguous}>{'Ambiguous'}</option>
              </select>
            </div>

            <div>
              <label htmlFor="mrn" className="form-label mb-1 block">
                MRN <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="mrn"
                name="mrn"
                value={patientData.mrn}
                onChange={handleInputChange}
                required
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="MRN"
              />
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="section-header border-b border-gray-200 pb-2">Collection</h3>

          <div className="space-y-3">
            <label className="form-label block">
              Collection Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label
                className={`flex cursor-pointer items-center rounded-lg border-2 p-4 transition-colors ${
                  patientData.collectionType === 'whole-blood-edta'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="collectionType"
                  value="whole-blood-edta"
                  checked={patientData.collectionType === 'whole-blood-edta'}
                  onChange={handleInputChange}
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="body-text ml-3">Whole Blood (EDTA)</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

export default PatientInformation
